import {useGSAP} from '@gsap/react';
import {gsap} from 'gsap';
import {debounce} from 'lodash';
import {
  ComponentType,
  CSSProperties,
  LazyExoticComponent,
  lazy,
  RefObject,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {useSelector} from 'react-redux';
import {CardNewProps} from '~/common/canvas/Card/CardNew';
import {useStickerCanvas} from '~/common/canvas/hooks/useStickerCanvas';
import {useInviteName} from '~/common/fabric/Card/components/AnimationPreview/hooks/useInviteName';
import {selectCardWidthHeight} from '~/common/fabric/reducers/eviteEvent/selectors';
import {FabricArt} from '~/common/fabric/types';
import {selectOrigin} from '~/ViewInvitation/selectors/eventDetails';
import {selectGuestName} from '~/ViewInvitation/selectors/guest';
import {
  selectEnvelope,
  selectEnvelopeStickers,
  selectLiner,
  selectStamp,
} from '~/ViewInvitation/selectors/premiumDesign';
import linerShading from '~images/liner_shading.png';
import {
  ENVELOPE_CARD_OFFSET,
  ENVELOPE_RATIO,
  ENVELOPE_RATIO_CLOSED,
  WRAPPER_PADDING_MW,
  WRAPPER_RATIO_MULTIPLIER,
} from './animation-config';
import {EnvelopeBottomBack} from './components/EnvelopeBottomBack';
import {EnvelopeBottomFront} from './components/EnvelopeBottomFront';
import {EnvelopeClosedFlap} from './components/EnvelopeClosedFlap';
import {EnvelopeOpenFlap} from './components/EnvelopeOpenFlap';
import {EnvelopeStampSide} from './components/EnvelopeStampSide';
import {ReplayButton} from './components/ReplayButton';
import {useAnimationSlopes} from './hooks/useAnimationSlopes';
import {useCardAnimation} from './hooks/useCardAnimation';

import './ClipPaths.sass';
import './CardAnimation.sass';

gsap.registerPlugin(useGSAP);

const CardNew: LazyExoticComponent<ComponentType<CardNewProps>> = lazy(() =>
  import('~/common/canvas/Card/CardNew').then((module) => ({default: module.CardNew}))
);

interface CardAnimationProps {
  fabricArt: FabricArt;
  isWebview?: boolean;
  noAnimation?: boolean;
  hasPlayedRef: RefObject<boolean>;
  onReplay: () => void;
  setAnimationComplete: () => void;
  ref?: RefObject<CardAnimationRef | null>;
}

export interface CardAnimationRef {
  play: () => void;
  reset: () => void;
}

export const CardAnimation = ({
  fabricArt,
  isWebview = false,
  noAnimation = false,
  hasPlayedRef,
  onReplay,
  setAnimationComplete,
  ref,
}: CardAnimationProps) => {
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number>(0);

  const guestName = useSelector(selectGuestName);
  const origin = useSelector(selectOrigin);
  const envelopeStickers = useSelector(selectEnvelopeStickers);
  const envelope = useSelector(selectEnvelope);
  const stamp = useSelector(selectStamp);
  const liner = useSelector(selectLiner);
  const {width: cardW, height: cardH} = useSelector(selectCardWidthHeight);

  const containerRef = useRef<HTMLDivElement>(null);

  const {canvasRef} = useStickerCanvas({
    configJson: envelopeStickers,
    onChange: () => null,
    setCanvas: () => null,
  });
  const inviteName = useInviteName({guestName, origin});

  const cardAspectRatio = cardH / cardW;
  /* Best way to test other aspect ratios, lets just keep these around */
  // const cardAspectRatio = 0.73529412; // Landscape
  // const cardAspectRatio = 1; // Square

  // Is the width of the parent container less than its height?
  const widthLessThanHeight = containerWidth < containerHeight * WRAPPER_RATIO_MULTIPLIER;

  // Difference between initial card width and envelope width
  const cardEnvelopeBuffer = containerHeight * ENVELOPE_CARD_OFFSET;

  const {cardScaleInit, envelopeScaleFinal, wrapperPaddingDesk} = useAnimationSlopes({
    cardAspectRatio,
  });

  const cardHeight = widthLessThanHeight
    ? (containerWidth - WRAPPER_PADDING_MW) * cardAspectRatio
    : containerHeight - wrapperPaddingDesk;
  const cardWidth = widthLessThanHeight
    ? containerWidth - WRAPPER_PADDING_MW
    : (containerHeight - wrapperPaddingDesk) / cardAspectRatio;

  // Envelope
  const envelopeWidth = cardWidth * cardScaleInit + cardEnvelopeBuffer;
  const envelopeHeight = envelopeWidth * ENVELOPE_RATIO;
  const envelopeHeightClosed = envelopeWidth * ENVELOPE_RATIO_CLOSED;

  const style: CSSProperties & Record<string, string | number> = {
    '--card-ratio': cardAspectRatio,
    '--card-scale-init': cardScaleInit,
    '--card-height': `${cardHeight}px`,
    '--card-width': `${cardWidth}px`,
    '--envelope-width': `${envelopeWidth}px`,
    '--envelope-height': `${envelopeHeight}px`,
    '--envelope-height-closed': `${envelopeHeightClosed}px`,
  };

  const {play, reset} = useCardAnimation({
    containerRef,
    containerWidth,
    containerHeight,
    cardWidth,
    envelopeHeight,
    noAnimation,
    envelopeScaleFinal,
    hasPlayedRef,
    setAnimationComplete,
  });

  useImperativeHandle(
    ref,
    () => ({
      play,
      reset,
    }),
    [play, reset]
  );

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(
      debounce((entries) => {
        const entry = entries[0];
        if (entry) {
          const {width, height} = entry.contentRect;
          setContainerWidth(width);
          setContainerHeight(height);
        }
      }, 10)
    );

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div className="card-animation-container" ref={containerRef} style={style}>
      <div className="wrapper">
        <EnvelopeOpenFlap envelope={envelope} liner={liner} linerShading={linerShading} />
        <EnvelopeBottomBack envelope={envelope} liner={liner} linerShading={linerShading} />
        <CardNew fabricArt={fabricArt} width={cardWidth} height={cardHeight} />
        <EnvelopeBottomFront envelope={envelope} />
        <EnvelopeClosedFlap envelope={envelope} />
        <EnvelopeStampSide
          envelope={envelope}
          stamp={stamp}
          inviteName={inviteName}
          canvasRef={canvasRef}
        />
      </div>
      {!isWebview && !noAnimation && <ReplayButton onReplayClick={onReplay} />}
    </div>
  );
};
